import { useParams } from 'react-router-dom';
import useCustomSearchParams from 'hooks/useCustomSearchParams';
import {
    orderBy, query, where,
} from 'firebase/firestore';
import { Column } from 'react-rainbow-components';
import { useEffect } from 'react';
import { isEmpty } from '@rainbow-modules/validation';
import useSessions from 'data/firestore/session/useCollectionWithPagination';
import DurationColumn from 'components/DurationColumn';
import DateTimeColumn from 'components/DateTimeColumn';
import Pagination from 'components/Pagination';
import Table from 'components/Table';
import useAlgoliaSearchData from 'data/algolia/useSearchData';
import ColumnTags from 'components/TableColumns/Tags';
import { ALGOLIA_INDEX_SESSIONS, PAGE_SIZE } from '../../../constants';
import {
    Container, EntriesText, PaginationContainer,
} from './styled';
import ConversationIdColumn from './columns/conversationId';
import UserColumn from './columns/user';
import StatusColumn from './columns/status';
import DirectionColumn from './columns/direction';
import HandledByColumn from './columns/handledBy';
import buildAlgoliaFilters from './helpers/buildAlgoliaFilters';

const ConversationsList = () => {
    const { agentId = '' } = useParams<{ agentId: string }>();

    const [searchParams, setSearchParams] = useCustomSearchParams({
        page: {
            type: 'number',
            default: 1,
        },
        search: {
            type: 'string',
            default: '',
        },
        date: {
            type: 'string',
            default: undefined as unknown as string,
        },
        duration: {
            type: 'string',
            default: undefined as unknown as string,
        },
        direction: {
            type: 'string',
            default: undefined as unknown as string,
        },
        status: {
            type: 'string',
            default: undefined as unknown as string,
        },
        tags: {
            type: 'string',
            default: undefined as unknown as string,
        },
        flags: {
            type: 'string',
            default: undefined as unknown as string,
        },
    });

    const { page } = searchParams;
    const hasSearchFilters = Object.entries(searchParams)
        .filter(
            ([key, value]) => key !== 'page' && !isEmpty(value),
        ).length > 0;

    const {
        data: sessions,
        isLoading: isLoadingSessions,
        totalRecords: totalSessions,
        error,
    } = useSessions({
        limit: PAGE_SIZE,
        listQueryFn: (ref) => query(ref, where('agentId', '==', agentId), orderBy('createdAt', 'desc')),
        track: [agentId],
        disabled: Boolean(
            hasSearchFilters || page > 1,
        ),
    });

    const algoliaData = useAlgoliaSearchData({
        search: searchParams.search,
        activePage: page,
        pageSize: PAGE_SIZE,
        indexName: ALGOLIA_INDEX_SESSIONS,
        filters: buildAlgoliaFilters({
            agentId,
            filters: searchParams,
        }),
        enabled: Boolean(hasSearchFilters || page > 1),
    });

    const handlePageChange = (event: React.ChangeEvent<unknown>, pageNumber: number) => {
        if (isLoadingSessions) return;
        setSearchParams({ page: pageNumber });
    };

    useEffect(() => {
        const totalPages = algoliaData?.nbPages;
        if (algoliaData.isLoading) return;
        if (totalPages === 0) {
            setSearchParams({ page: undefined }, true);
        }
        if (
            totalPages !== undefined
            && totalPages > 0
            && page > totalPages
        ) {
            setSearchParams({ page: 1 }, true);
        }
    }, [
        algoliaData?.nbPages,
        page,
        setSearchParams,
        algoliaData.isLoading,
    ]);

    if (error || (!sessions && !isLoadingSessions)) {
        return (
            <Container>
                <h1>Something went wrong</h1>
                {error?.message && (
                    <p>
                        {error.name}
                        :
                        {' '}
                        {error.code}
                    </p>
                )}
            </Container>
        );
    }
    const dataToShow = algoliaData?.hits || sessions;
    const totalHits = typeof algoliaData?.nbHits === 'number' ? algoliaData.nbHits : totalSessions;
    const pages = Math.ceil(totalHits / PAGE_SIZE);
    const firstItemOfActivePage = dataToShow.length === 0
        ? 0
        : PAGE_SIZE * (page - 1) + 1;
    const lastItemOfActivePage = Math.min(
        PAGE_SIZE * page,
        (PAGE_SIZE * page - PAGE_SIZE) + dataToShow.length,
    );

    return (
        <Container>
            <Table
                data={dataToShow}
                keyField="id"
                isLoading={isLoadingSessions}
            >
                <Column header="ID" field="id" component={ConversationIdColumn} defaultWidth={100} />
                <Column header="Dir" field="direction" component={DirectionColumn} defaultWidth={80} />
                <Column header="Customer" component={UserColumn} />
                <Column header="Date" field="createdAt" component={DateTimeColumn} defaultWidth={180} />
                <Column header="Duration" field="duration" component={DurationColumn} cellAlignment="right" defaultWidth={120} />
                <Column header="Handled By" component={HandledByColumn} />
                <Column header="Status" field="status" component={StatusColumn} defaultWidth={110} />
                <Column header="Tags" field="tags" component={ColumnTags} agentId={agentId} entity="call" />
            </Table>
            <PaginationContainer>
                <EntriesText>
                    Showing
                    {' '}
                    {firstItemOfActivePage}
                    {' '}
                    to
                    {' '}
                    {lastItemOfActivePage}
                    {' '}
                    of
                    {' '}
                    {totalHits}
                    {' '}
                    entries.
                </EntriesText>
                <Pagination
                    activePage={searchParams.page}
                    pages={pages}
                    onChange={handlePageChange}
                />
            </PaginationContainer>
        </Container>
    );
};

export default ConversationsList;
